/**
 * The sort rule every sortable table shares: a column opened for the first
 * time starts in its natural order (names and words ascending, numbers and
 * dates highest or newest first), and a second click on the same column flips
 * it. `useSort` holds the choice in the browser; `sortPatch` computes the
 * change for tables whose sort lives in the URL and runs on the server.
 * Pass `ascendingFirst` as a module constant: a new Set per render would
 * rebuild `onSort` every time.
 */

import { useCallback, useState } from 'react';

import type { SortOrder } from './sortRows';

export interface SortState {
  sort: string;
  order: SortOrder;
}

/** The change a click on `key` makes to `current`. */
export function sortPatch(current: SortState, key: string, ascendingFirst: ReadonlySet<string>): SortState {
  if (current.sort === key) return { sort: key, order: current.order === 'asc' ? 'desc' : 'asc' };
  return { sort: key, order: ascendingFirst.has(key) ? 'asc' : 'desc' };
}

export function useSort(defaultKey: string, defaultOrder: SortOrder, ascendingFirst: ReadonlySet<string>) {
  const [state, setState] = useState<SortState>({ sort: defaultKey, order: defaultOrder });
  const onSort = useCallback(
    (key: string) => setState((current) => sortPatch(current, key, ascendingFirst)),
    [ascendingFirst],
  );
  return { sort: state.sort, order: state.order, onSort };
}
